import { loadAlpineAsync } from '@/src/utilities/script-loader';

export async function load() {
  if (!document.querySelector('[data-app="company-contacts-filter"]')) {
    return;
  }

  /** @type {InitState} */
  const initState = window.companyContacts;

  const Alpine = await loadAlpineAsync();

  const appState = {
    filters: {
      stateProvince: {
        qsKey: 'st',
        selectedTaxId: '0',
        filterTitle: initState.content.stateProvince.title,
        filterOptions: prepFilterOptions(
          initState.content.stateProvince.taxonomies,
        ),
        isSelected() {
          return isSelected(this);
        },
      },
      division: {
        qsKey: 'di',
        selectedTaxId: '0',
        filterTitle: initState.content.division.title,
        filterOptions: prepFilterOptions(initState.content.division.taxonomies),
        isSelected() {
          return isSelected(this);
        },
      },
      contactType: {
        qsKey: 'co',
        selectedTaxId: '0',
        filterTitle: initState.content.contactType.title,
        filterOptions: prepFilterOptions(
          initState.content.contactType.taxonomies,
        ),
        isSelected() {
          return isSelected(this);
        },
      },
      solutionType: {
        qsKey: 'so',
        selectedTaxId: '0',
        filterTitle: initState.content.solutionType.title,
        filterOptions: prepFilterOptions(
          initState.content.solutionType.taxonomies,
        ),
        isSelected() {
          return isSelected(this);
        },
      },
    },
    contacts: initState.contactData,
    onChangeFilter,
    onClickRemoveBadge,
    decodeEmail,
  };

  loadUrlFilterSelections(appState);

  Alpine.store('contacts', appState);

  Alpine.start();

  /**
   * Because the browser can load the page from memory using
   * the bfcache (https://web.dev/bfcache/), we need to ensure
   * changes in the URL (back/forward navigation) result in
   * changes to the state of the app.
   * The 'popstate' event is emitted every time the back/foward
   * navigation is used in the browser, but not when we use
   * history.pushState()
   */
  window.addEventListener('popstate', () =>
    loadUrlFilterSelections(Alpine.store('contacts')),
  );
}

/**
 * Takes the basic 'taxonomies' object hash from serialized server object and adds an 'isVisible' property
 * on each filter option that can then be used when the records are bound to the select options UI
 * @param {{ [key: string]: string}} initTax
 * @returns {{ [key: string]: FilterOption}}
 */
function prepFilterOptions(initTax) {
  // 1. Convert the object hash to an array of arrays
  // 2. Modify the 'value' array to include a new 'isVisible' boolean property
  // 3. Convert the array back to an object hash
  return Object.fromEntries(
    Object.entries(initTax).map(([k, v]) => [k, { name: v, isVisible: true }]),
  );
}

/**
 * Updates the isVisible property of every Contact, based on currently selected taxonomy filter values.
 * @param {State} appState
 */
function updateContactsVisibility(appState) {
  const anyFilterSelected =
    isSelected(appState.filters.stateProvince) ||
    isSelected(appState.filters.division) ||
    isSelected(appState.filters.solutionType) ||
    isSelected(appState.filters.contactType);

  // If no filters are applied, all contacts are visible
  if (!anyFilterSelected) {
    for (const c in appState.contacts) {
      appState.contacts[c].isVisible = true;
    }
  } // Otherwise, compare each contact's taxonomies against selections
  else {
    // Need an explicit conversion to int/number, because the ID is originally set as a string by x-model
    const stateId = parseTaxonomy(appState.filters.stateProvince);
    const divisionId = parseTaxonomy(appState.filters.division);
    const contactTypeId = parseTaxonomy(appState.filters.contactType);
    const solutionTypeId = parseTaxonomy(appState.filters.solutionType);

    for (const c in appState.contacts) {
      appState.contacts[c].isVisible =
        (!stateId || appState.contacts[c].taxonomies.includes(stateId)) &&
        (!divisionId || appState.contacts[c].taxonomies.includes(divisionId)) &&
        (!contactTypeId ||
          appState.contacts[c].taxonomies.includes(contactTypeId)) &&
        (!solutionTypeId ||
          appState.contacts[c].taxonomies.includes(solutionTypeId));
    }
  }
}

function onChangeFilter() {
  onChangeFilterState(this);
}

/**
 * Handler for onClick of a filter selection badge; will reset/remove the selected filter for
 * the specified filter type.
 * @this {State}
 * @param {*} filterType
 */
function onClickRemoveBadge(filterType) {
  this.filters[filterType].selectedTaxId = '0';
  onChangeFilterState(this);
}

/**
 * Triggers updates of state models that are bound to the UI.
 * Called by handlers for onChange of <select> and onClick of removal badges.
 * @param {State} appState
 */
function onChangeFilterState(appState) {
  updateContactsVisibility(appState);
  updateUrlFromFilters(appState);
  updateAvailableFilterOptions(appState);
}

/**
 * Evaluates all filter selections specified on the querystring and stores them in State.
 * Called by main load() method.
 * @param {State} appState
 */
function loadUrlFilterSelections(appState) {
  // Parse current querystring and set filters
  const searchParams = new URL(window.location.href).searchParams;
  const update = updateConfigFromQuery(searchParams);

  update(appState.filters.stateProvince);
  update(appState.filters.division);
  update(appState.filters.contactType);
  update(appState.filters.solutionType);

  updateContactsVisibility(appState);
  updateAvailableFilterOptions(appState);
}

/**
 * Determines all contextually applicable/visible filter options
 * @param {State} appState
 */
function updateAvailableFilterOptions(appState) {
  // Get the comprehensive set of taxonomy IDs represented by all 'visible' contacts
  const contactTaxonomies = [];
  for (const c in appState.contacts) {
    if (appState.contacts[c].isVisible) {
      contactTaxonomies.push(...appState.contacts[c].taxonomies);
    }
  }
  // Now go through each of the filter options, seeing if the option's taxonomy value is represented at least once
  for (const f in appState.filters) {
    for (const o in appState.filters[f].filterOptions) {
      appState.filters[f].filterOptions[o].isVisible =
        contactTaxonomies.includes(parseInt(o, 10));
    }
  }
}

/**
 * Leverages history.pushState() to ensure querystring parameters are always representative
 * of current filter selections. Uses querystring key names defined in State.
 * @param {State} appState
 */
function updateUrlFromFilters(appState) {
  // Evaluate the currently selected filters and set the URL querystring
  if (appState && appState.filters) {
    const url = new URL(window.location.href);

    // Iterate through each filter, and set/replace the new querystring value if applicable;
    //  or delete the key if no filter is applied
    for (const f in appState.filters) {
      /** @type {FilterConfig} */
      const currentFilter = appState.filters[f];

      updateQueryFromConfig(url.searchParams, currentFilter);
    }

    history.pushState(null, 'Company Contacts', url.toString());
  }
}

/**
 * Called by onclick event of Email link. Will decode the email value and keep it in the contact store.
 * @this {State}
 * @param {string} encodedEmail
 * @returns {string}
 */
function decodeEmail(encodedEmail, decodedEmail) {
  // If this contact's email is already decoded in the store, then return it
  if (decodedEmail) {
    return decodedEmail;
  }

  // Holds the final output
  let email = '';

  // Extract the first 2 letters
  const keyInHex = encodedEmail.substr(0, 2);

  // Convert the hex-encoded key into decimal
  const key = parseInt(keyInHex, 16);

  // Loop through the remaining encoded characters in steps of 2
  for (let n = 2; n < encodedEmail.length; n += 2) {
    // Get the next pair of characters
    const charInHex = encodedEmail.substr(n, 2);

    // Convert hex to decimal
    const char = parseInt(charInHex, 16);

    // XOR the character with the key to get the original character
    const output = char ^ key;

    // Append the decoded character to the output
    email += String.fromCharCode(output);
  }
  return email;
}

/**
 * @param {FilterConfig} config
 * @returns {number}
 */
function parseTaxonomy(config) {
  return config.selectedTaxId
    ? parseInt(config.selectedTaxId.toString(), 10)
    : 0;
}

/**
 * @param {FilterConfig} config
 * @returns {boolean}
 */
function isSelected(config) {
  return !!config.selectedTaxId && config.selectedTaxId !== '0';
}

/**
 * @param {URLSearchParams} params
 * @returns {(config: FilterConfig) => void}
 */
function updateConfigFromQuery(params) {
  return (config) => {
    config.selectedTaxId = params.get(config.qsKey) || '0';
  };
}

/**
 *
 * @param {URLSearchParams} params
 * @param {FilterConfig} config
 * @returns {void}
 */
function updateQueryFromConfig(params, config) {
  if (isSelected(config)) {
    params.set(config.qsKey, config.selectedTaxId);
  } else {
    params.delete(config.qsKey);
  }
}

/**
 * Main top-level object created in .cshtml from server objects
 * @typedef InitState
 * @property {{ [key: string]: ContactModel }} contactData
 * @property {InitContent} content
 */

/**
 * Group of all server-serialized filter config
 * @typedef InitContent
 * @property {InitFilterContent} stateProvince
 * @property {InitFilterContent} division
 * @property {InitFilterContent} contactType
 * @property {InitFilterContent} solutionType
 */

/**
 * Server-serialized config for a single filter
 * @typedef InitFilterContent
 * @property {string} title
 * @property {{ [key: string]: string}} taxonomies
 */

/**
 * @typedef InitFilterTaxonomy
 * @property {string} name
 * @property {boolean} isVisible
 */

/**
 * Represents one Contact in app state
 * @typedef ContactModel
 * @property {number[]} taxonomies
 * @property {string} encodedEmail
 * @property {string} decodedEmail
 * @property {boolean} isVisible
 */

/**
 * Comprehensive set of all data for a specific filter (e.g., division)
 * @typedef FilterConfig
 * @property {string} qsKey - Name of querystring key containing filter value
 * @property {string} selectedTaxId - String version of integer taxonomy/category ID currently selected for this filter
 * @property {string} filterTitle
 * @property {{ [key: string]: FilterOption}} filterOptions - Serialized only to make it easier to retrieve the selected option text for badges
 */

/**
 * Group of all filters and their configurations
 * @typedef ContactFilters
 * @property {FilterConfig} stateProvince
 * @property {FilterConfig} division
 * @property {FilterConfig} contactType
 * @property {FilterConfig} solutionType
 */

/**
 * Definition of a single taxonomy option within a filter. e.g.
 * @typedef FilterOption
 * @property {string} name
 * @property {boolean} isVisible
 */

/**
 * @typedef State
 * @property {ContactFilters} filters
 * @property {{ [key: string]: ContactModel }} contacts - Object hash, where contacts[contactId] = ContactModel
 */
